import { IconButton, InputAdornment, makeStyles } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import {
  DesktopDateTimePicker as DateTime,
  DateTimeValidationError,
  FieldSelectedSections,
} from '@mui/x-date-pickers';
import { PickerChangeHandler } from '@mui/x-date-pickers/internals/hooks/usePicker/usePickerValue';
import cn from 'classnames';
import { useState, useCallback, useRef } from 'react';
import { COLORS } from '../../theme/variables';
import { TestId } from '../Testing/TestId';
import { TextFieldProps } from './text-field';

interface DateInputProps {
  value: Date | null;
  onChange: PickerChangeHandler<Date | null, DateTimeValidationError>;
  onBlur: () => void;
  disablePast?: boolean;
  textFieldProps?: TextFieldProps;
  disabled?: boolean;
  onClose: () => void;
  className?: string;
  error?: boolean;
  inputValue?: string;
  minDate?: Date;
  maxDate?: Date;
  testId?: string;
  onFocus?: () => void;
  readOnly?: boolean;
}

const useStyles = makeStyles({
  container: {
    '& .MuiFormLabel-root, .MuiInputBase-input': {
      fontFamily: 'Inter, Roboto, "Helvetica Neue", Arial, sans-serif',
      fontSize: 14,
      fontWeight: 400,
    },
    '& .MuiFormLabel-root': {
      color: COLORS.COLOR_TEXT_LIGHTENED_20,
      lineHeight: '160%',
      '&.MuiInputLabel-shrink': {
        lineHeight: '100%',
      },
    },
    '& .MuiInputBase-input': {
      height: 19,
      lineHeight: '118%',
      paddingTop: '18.5px',
      paddingBottom: '18.5px',
    },
    '& .MuiFormHelperText-root.Mui-error': {
      display: 'none',
    },
  },
  error: {
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: `${COLORS.COLOR_RED_BASE} !important`,
    },
    '& .MuiFormLabel-root': {
      color: `${COLORS.COLOR_RED_BASE} !important`,
    },
  },
  small: {
    '& .MuiFormLabel-root:not(.MuiInputLabel-shrink)': {
      fontSize: 14,
      transform: 'translate(14px, 8px) scale(1)',
    },
    '& .MuiInputBase-input': {
      height: 38,
      paddingTop: 10,
      paddingBottom: 10,
      boxSizing: 'border-box',
    },
  },
});

export function DateTimePicker({
  value,
  onChange,
  disablePast,
  textFieldProps,
  disabled,
  onBlur,
  onClose,
  className,
  testId,
  error,
  onFocus,
  readOnly,
  ...rest
}: DateInputProps) {
  const classes = useStyles();
  const datePickerRef = useRef<HTMLDivElement>(null);
  const [focus, setFocus] = useState(false);

  const handleSelectedSectionsChange = useCallback(
    (selectedSection: FieldSelectedSections) => {
      if (selectedSection !== null && !focus) {
        setFocus(true);
        onFocus && onFocus();
      } else if (selectedSection === null && focus) {
        setFocus(false);
        onBlur && onBlur();
      }
    },
    [focus, onBlur, onFocus],
  );

  const handleClear = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onChange(null, { validationError: null });
    },
    [onChange],
  );

  return (
    <TestId testId={testId || ''}>
      <DateTime
        {...rest}
        disabled={disabled}
        readOnly={readOnly}
        disableOpenPicker={readOnly}
        className={cn(classes.container, className, {
          [classes.error]: error,
          [classes.small]: textFieldProps?.small,
        })}
        aria-invalid={error}
        value={value}
        onChange={(value, context) => {
          if (readOnly) {
            return;
          }
          value?.setSeconds(0, 0);
          onChange(value, context);
        }}
        onSelectedSectionsChange={handleSelectedSectionsChange}
        onClose={onClose}
        disablePast={disablePast}
        format='MM/dd/yyyy hh:mm a'
        ref={datePickerRef}
        slotProps={{
          inputAdornment: {
            position: 'start',
          },
          textField: (ownerState) => ({
            ...ownerState,
            InputProps: {
              ...ownerState.InputProps,
              endAdornment:
                value && !disabled && !textFieldProps?.readOnly ? (
                  <InputAdornment position='end'>
                    <IconButton
                      aria-label='clear date'
                      onClick={handleClear}
                      edge='end'
                      size='small'
                      disabled={disabled || textFieldProps?.readOnly}>
                      <CloseIcon fontSize='small' />
                    </IconButton>
                  </InputAdornment>
                ) : (
                  ownerState.InputProps?.endAdornment
                ),
            },
          }),
        }}
      />
    </TestId>
  );
}

export default DateTimePicker;
